// ===================================================================
// VOLUME DISCOUNT TIERS - Helper condiviso (collection, search bar, related products)
// ===================================================================
// Mostra le percentuali di sconto quantità (es. 3/-10%, 6/-15%, 12/-20%) sotto
// il prezzo nelle card prodotto, per i prodotti con tag "_qty_disc_*".
//
// I dati NON vengono letti da Algolia: il metafield volume_discount.tiers
// (scritto dall'app XtraVolumeDiscount) è esposto alla Storefront API tramite
// una metafield definition (namespace volume_discount, key tiers, ownerType
// PRODUCT) con accesso storefront abilitato. Qui leggiamo quel metafield in
// batch per le card, riusando il client Storefront gia' presente in
// storefront-api.js.
//
// Contratto con i renderer delle card (algolia-collection-rendering.js,
// algolia-search.js, algolia-related-products.js):
// - Per ogni prodotto con hasQtyDiscountTag === true, inserire nel markup:
//     <div class="volume-discount-tiers" data-product-id="${productId}"></div>
//   subito sotto il blocco prezzo.
// - Dopo aver inserito le card nel DOM, chiamare:
//     AlgoliaVolumeDiscountTiers.enhance(containerElement)
//   L'helper trova da solo i placeholder non ancora processati, fa UNA fetch
//   batch, e li riempie. Idempotente: sicuro da richiamare più volte.
//   IMPORTANTE: se il container viene popolato tramite un requestAnimationFrame
//   differito (es. AlgoliaPerformanceUtils.optimizedRender), enhance() va
//   chiamata DOPO che l'HTML e' realmente nel DOM (vedi setTimeout usato da
//   fixBadgesPositioning in algolia-collection.js per lo stesso motivo).
const AlgoliaVolumeDiscountTiers = (function () {
  const CACHE = new Map(); // productId (string) -> tiers risolte per il mercato corrente ([] se nessuna)
  const IN_FLIGHT = new Map(); // productId -> Promise condivisa della fetch batch in corso

  function getCurrentMarketHandle() {
    return String(window.shopifyCurrentMarket || "").toLowerCase();
  }

  function toGid(productId) {
    return `gid://shopify/Product/${productId}`;
  }

  function fromGid(gid) {
    const match = /\/Product\/(\d+)/.exec(gid || "");
    return match ? match[1] : null;
  }

  function buildQuery() {
    return `
      query VolumeDiscountTiers($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on Product {
            id
            metafield(namespace: "volume_discount", key: "tiers") {
              value
            }
          }
        }
      }
    `;
  }

  // Estrae dal JSON del metafield le tier valide per il mercato corrente,
  // ordinate per quantità crescente, con il flag "highlighted" sulla tier
  // con lo sconto percentuale più alto (non necessariamente l'ultima).
  function parseTiersForCurrentMarket(rawValue) {
    if (!rawValue) return [];

    let data;
    try {
      data = JSON.parse(rawValue);
    } catch (e) {
      return [];
    }

    const rules = Array.isArray(data?.rules) ? data.rules : [];
    const currentMarket = getCurrentMarketHandle();

    const rule = rules.find(
      (r) =>
        Array.isArray(r?.markets) &&
        r.markets.some((m) => String(m).toLowerCase() === currentMarket),
    );

    if (!rule || !Array.isArray(rule.tiers) || rule.tiers.length === 0) {
      return [];
    }

    const sorted = rule.tiers
      .filter((t) => t && t.min_qty > 0 && t.discount_pct > 0)
      .sort((a, b) => a.min_qty - b.min_qty);

    if (sorted.length === 0) return [];

    // Gradiente a 3 livelli sulle pillole, in base allo sconto: la tier con lo
    // sconto piu' basso resta "low" (stile chiaro), quella con lo sconto piu'
    // alto diventa "high" (stile scuro), le eventuali tier intermedie sono "mid".
    const discountValues = sorted.map((t) => t.discount_pct);
    const minDiscount = Math.min(...discountValues);
    const maxDiscount = Math.max(...discountValues);

    return sorted.map((t) => {
      let level;
      if (t.discount_pct === maxDiscount) {
        level = "high";
      } else if (t.discount_pct === minDiscount) {
        level = "low";
      } else {
        level = "mid";
      }
      return {
        min_qty: t.min_qty,
        discount_pct: t.discount_pct,
        level,
      };
    });
  }

  // Fa la fetch batch (Storefront API) delle tier per gli id prodotto passati,
  // usando la cache/in-flight map per non richiedere due volte lo stesso prodotto.
  async function fetchTiers(productIds) {
    const uniqueIds = [...new Set((productIds || []).map(String))].filter(Boolean);
    const idsToFetch = uniqueIds.filter((id) => !CACHE.has(id) && !IN_FLIGHT.has(id));

    if (idsToFetch.length > 0) {
      const fetchPromise = (async () => {
        try {
          const gids = idsToFetch.map(toGid);
          const response = await window.ShopifyStorefront.executeQuery(buildQuery(), {
            ids: gids,
          });
          if (response?.errors) {
            console.warn("[VDT] risposta Storefront con errori GraphQL:", response.errors);
          }
          const nodes = response?.data?.nodes || [];
          nodes.forEach((node) => {
            if (!node) return;
            const id = fromGid(node.id);
            if (!id) return;
            CACHE.set(id, parseTiersForCurrentMarket(node.metafield?.value));
          });
        } catch (e) {
          console.warn("[VDT] fetch delle tier fallita:", e);
        } finally {
          // Anche in caso di errore, marca come "risolti senza tier" per non
          // ritentare all'infinito sugli stessi prodotti in questa sessione.
          idsToFetch.forEach((id) => {
            if (!CACHE.has(id)) CACHE.set(id, []);
            IN_FLIGHT.delete(id);
          });
        }
      })();

      idsToFetch.forEach((id) => IN_FLIGHT.set(id, fetchPromise));
    }

    await Promise.all(uniqueIds.map((id) => IN_FLIGHT.get(id)).filter(Boolean));

    const result = {};
    uniqueIds.forEach((id) => {
      result[id] = CACHE.get(id) || [];
    });
    return result;
  }

  // Silhouette di una singola bottiglia, ritagliata dall'icona "3 bottiglie"
  // già introdotta per il badge (vedi algolia-collection-rendering.js).
  // fill="currentColor" cosi' la pillola normale/evidenziata la colora da sola.
  function bottleIconSvg() {
    return (
      '<svg class="vdt-pill__icon" viewBox="18 -3 76 247" xmlns="http://www.w3.org/2000/svg" fill="currentColor" aria-hidden="true">' +
      '<path d="M50.3735 0.167838C50.3735 0.167838 42.9554 1.15062 43.8456 6.45756C44.7357 11.7647 44.5379 19.9215 44.0434 21.8871C43.5488 23.8526 42.7575 26.8993 43.9445 28.2751C45.1314 29.6511 45.1324 32.1286 45.1324 32.1286C48.1826 72.539 34.4566 113.202 28.1019 132.144C21.7473 151.087 22.0014 166.746 22.0014 166.746V230.699C22.0014 240.855 34.2023 241 34.2023 241H78.1583C78.1583 241 90.3592 240.855 90.3592 230.699V166.746C90.3592 166.746 90.6135 151.087 84.2588 132.144C77.9042 113.202 64.1781 72.539 67.2283 32.1286C67.2283 32.1286 67.2294 29.6511 68.4163 28.2751C69.6031 26.8993 68.8118 23.8526 68.3174 21.8871C67.8228 19.9215 67.6249 11.7647 68.5152 6.45756C69.4054 1.15062 61.9873 0.167838 61.9873 0.167838C61.9873 0.167838 54.0202 -0.209798 50.3735 0.167838Z"/>' +
      "</svg>"
    );
  }

  function renderPillsHtml(tiers) {
    if (!tiers || tiers.length === 0) return "";

    const pills = tiers
      .map((t) => {
        const levelClass =
          t.level === "high"
            ? " vdt-pill--high"
            : t.level === "mid"
              ? " vdt-pill--mid"
              : "";
        return `
        <span class="vdt-pill${levelClass}">
          <span class="vdt-pill__qty">${t.min_qty}</span>
          ${bottleIconSvg()}
          <span class="vdt-pill__pct">-${t.discount_pct}%</span>
        </span>`;
      })
      .join("");

    return `<div class="volume-discount-tiers__row">${pills}</div>`;
  }

  // Trova i placeholder non ancora processati dentro `root`, fa la fetch batch
  // e li riempie. Idempotente: i placeholder gia' fatti vengono ignorati.
  async function enhance(root) {
    const scope = root || document;
    const placeholders = Array.from(
      scope.querySelectorAll(
        ".volume-discount-tiers[data-product-id]:not([data-vdt-done])",
      ),
    );

    if (placeholders.length === 0) return;

    const productIds = placeholders.map((el) => el.getAttribute("data-product-id"));
    const tiersById = await fetchTiers(productIds);

    placeholders.forEach((el) => {
      const id = el.getAttribute("data-product-id");
      el.setAttribute("data-vdt-done", "true");
      el.innerHTML = renderPillsHtml(tiersById[id]);
    });
  }

  return {
    enhance,
    fetchTiers,
    renderPillsHtml,
    getCurrentMarketHandle,
  };
})();

window.AlgoliaVolumeDiscountTiers = AlgoliaVolumeDiscountTiers;
